import { Card, CardContent, Button, Badge } from '@quadminds/mindkit';
import { Copy, Loader2, X } from 'lucide-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useQuestionPage from '../../../hooks/useQuestionPage';
import { shouldHideSuggestedQuestions } from '../../../utils/utils';
import { BadgeCustom } from '../../common/CustomBadge/CustomBadge';
import SendFeedbackDialog from '../../dialogs/SendFeedbackDialog';

const DAYS_DIFF = 7;

const QuestionPage = () => {
    const {
        filteredCategories,
        setIsDialogOpen,
        isDialogOpen,
        feedback,
        setFeedback,
        isSubmitting,
        handleSubmit,
        handleClose,
        userInfo,
        isImpersonated,
        getQuersionsQuery,
        handleCopyQuestion,
    } = useQuestionPage();

    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [showAllQuestions, setShowAllQuestions] = useState<Record<string, boolean>>({});
    const navigate = useNavigate();

    const toggleCategory = (categoryId: string) => {
        setSelectedCategories((prev) => {
            if (prev.includes(categoryId)) {
                return prev.filter((id) => id !== categoryId);
            } else {
                return [...prev, categoryId];
            }
        });
    };

    const clearAllFilters = () => {
        setSelectedCategories([]);
    };

    const toggleShowAll = (categoryId: string) => {
        setShowAllQuestions((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }));
    };

    const getVisibleQuestions = (questions: any[], categoryId: string) => {
        if (selectedCategories.length > 0) return questions;

        // Mostrar solo las primeras 5 preguntas cuando no hay filtros seleccionados
        const questionsToShow = showAllQuestions[categoryId] ? questions : questions.slice(0, 5);
        return questionsToShow;
    };

    const displayedCategories =
        selectedCategories.length > 0
            ? filteredCategories?.filter((cat) => selectedCategories.includes(cat.id))
            : filteredCategories;
    const hideSuggestedQuestions = shouldHideSuggestedQuestions(userInfo);

    useEffect(() => {
        if (hideSuggestedQuestions) {
            navigate(-1);
        }
    }, [hideSuggestedQuestions, navigate]);

    if (hideSuggestedQuestions) {
        return null;
    }

    return (
        <div className="flex flex-col lg:flex-row h-full w-full bg-background gap-4 py-4 px-8">
            <div className="flex-1 flex flex-col lg:w-2/3 overflow-hidden">
                <div className="flex flex-col pb-6">
                    <h1 className="font-poppins font-semibold text-xl text-foreground">Guía de Preguntas</h1>
                    <p className="text-muted-foreground text-sm">Ejemplos de lo que puedes preguntar a Mindy</p>
                </div>

                {getQuersionsQuery.isLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <>
                        <div className="py-4 mb-6 border-y">
                            <div className="flex flex-wrap items-center gap-2">
                                <h2 className="font-poppins text-xs uppercase text-muted-foreground">Filtrar</h2>
                                {getQuersionsQuery.data?.map((category) => {
                                    const isSelected = selectedCategories.includes(category.id);
                                    return (
                                        <Badge
                                            key={category.id}
                                            variant={isSelected ? 'default' : 'outline'}
                                            className="cursor-pointer px-2.5 py-1.5 text-xs font-normal font-poppins transition-all hover:scale-105"
                                            onClick={() => toggleCategory(category.id)}
                                        >
                                            <span className="mr-2">{category.icon}</span>
                                            {category.title}
                                        </Badge>
                                    );
                                })}
                                {selectedCategories.length > 0 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={clearAllFilters}
                                        className="h-8 px-2 text-muted-foreground hover:text-foreground"
                                    >
                                        <X className="w-4 h-4 mr-1" />
                                        Limpiar filtros
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto pr-2">
                            <div className="space-y-8">
                                {displayedCategories?.map((category) => {
                                    const isAllView = selectedCategories.length === 0;
                                    const visibleQuestions = getVisibleQuestions(category.questions, category.id);
                                    const hasMore = category.questions.length > 5;
                                    const showingAll = showAllQuestions[category.id];

                                    return (
                                        <div key={category.id}>
                                            <div className="flex items-center gap-3 mb-4">
                                                <span className="text-lg">{category.icon}</span>
                                                <h2 className="font-poppins font-semibold text-foreground">
                                                    {category.title}
                                                </h2>
                                            </div>

                                            <div className="relative">
                                                <div className="flex flex-wrap gap-3">
                                                    {visibleQuestions.map((question, index) => (
                                                        <Card
                                                            key={index}
                                                            onClick={() => handleCopyQuestion(question.text)}
                                                            className="group cursor-pointer transition-all duration-200 bg-gray-qm-50 dark:bg-gray-qm-900 border border-gray-qm-100 dark:border-gray-qm-800 hover:bg-gray-qm-100 hover:dark:bg-gray-qm-800 hover:border-gray-qm-300 hover:dark:border-gray-qm-700 hover:shadow-md"
                                                        >
                                                            <CardContent className="flex space-x-2 items-center py-1.5 px-2.5 relative">
                                                                <p className="text-sm">{question.text}</p>
                                                                {question.is_new &&
                                                                    moment(question.created_at).isAfter(
                                                                        moment().subtract(DAYS_DIFF, 'days'),
                                                                    ) && (
                                                                        <BadgeCustom className="!h-4" variant="primary">
                                                                            Nueva
                                                                        </BadgeCustom>
                                                                    )}
                                                                <div className="pointer-events-none absolute right-0 top-0 bottom-0 -left-2 bg-gray-100/0 dark:bg-gray-qm-900/0 group-hover:bg-gray-100/90 group-hover:dark:bg-gray-qm-900/90 transition-opacity duration-200 hidden group-hover:flex items-center justify-center rounded-lg">
                                                                    <Copy className="w-4 h-4 text-primary dark:text-tertiary-qm-100" />
                                                                </div>
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </div>

                                                {isAllView && hasMore && !showingAll && (
                                                    <div className="relative mt-3">
                                                        <div className="absolute inset-x-0 -top-20 h-20 bg-gradient-to-b from-transparent to-background pointer-events-none" />
                                                        <div className="flex">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => toggleShowAll(category.id)}
                                                                className="relative z-1 !text-xs text-tertiary-qm-300"
                                                            >
                                                                Ver más ({category.questions.length - 6} más)
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}

                                                {isAllView && showingAll && hasMore && (
                                                    <div className="flex mt-4">
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="!text-xs text-tertiary-qm-200"
                                                            onClick={() => toggleShowAll(category.id)}
                                                        >
                                                            Ver menos
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="lg:w-1/4 flex-shrink-0 max-lg:border-t max-lg:pt-4 lg:border-l lg:pl-4 border-gray-200 dark:border-gray-700">
                <Card className="bg-card dark:bg-card border-2 h-fit sticky top-0">
                    <CardContent className="p-6 flex flex-col items-center text-center">
                        <h3 className="text-base font-poppins font-medium text-foreground mb-4">
                            ¿Tienes sugerencias?
                        </h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            Nos encantaría escuchar tus ideas para mejorar
                        </p>
                        <Button size="sm" className="w-full" onClick={() => setIsDialogOpen(true)}>
                            Enviar Sugerencia
                        </Button>
                    </CardContent>
                </Card>
            </div>
            <SendFeedbackDialog
                isDialogOpen={isDialogOpen}
                handleClose={handleClose}
                feedback={feedback}
                setFeedback={setFeedback}
                isSubmitting={isSubmitting}
                handleSubmit={() => {
                    handleSubmit('question');
                }}
                userInfo={userInfo}
                isImpersonated={isImpersonated}
            />
        </div>
    );
};

export default QuestionPage;
